import {Component, OnInit} from '@angular/core';
import {ItemAvailability, StructItemBaseTyped, V3Chapter, V3ExerciseSession, V3ExerciseSet} from "../../../cspa/model/v3/cspa";
import {Page, Pageable} from "../../../utils/pageable";
import {ActivatedRoute} from "@angular/router";
import {combineLatest, from, map, mergeAll, Observable, of, switchMap, tap} from "rxjs";
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {SpinnerService} from "../../../utils/services/spinner.service";
import {V3Student} from "../../../auth_profile/model/v3/schools";

@Component({
    selector: 'app-teacher-student-cspa-monitor',
    templateUrl: './teacher-student-cspa-monitor.component.html',
    styleUrls: ['./teacher-student-cspa-monitor.component.scss'],
    standalone: false
})
export class TeacherStudentCspaMonitorComponent implements OnInit {

  schoolId: number;
  exerciseSets: Map<string, V3ExerciseSet> = null;
  chaptersCache = new Map<string, V3Chapter[]>();
  setAvailabilities: ItemAvailability[] = [];
  innerAvailabilities: Map<string, ItemAvailability> = new Map<string, ItemAvailability>();
  private student: V3Student;
  private setSessions: Map<string, Page<V3ExerciseSession>> = new Map<string, Page<V3ExerciseSession>>();

  constructor(
    private route: ActivatedRoute,
    private provider: NativeServiceApiProvider,
    private authService: AuthorizationServiceProvider,
    private spinnerService: SpinnerService
  ) {
  }

  ngOnInit() {
    this.loadSchoolId().pipe(
      switchMap(_ => this.loadStudent()),
      switchMap(_ => this.loadExerciseSets()),
      switchMap(_ => this.loadData())
    ).subscribe();
  }


  private loadSchoolId() {
    return this.authService.getAuthDetailsService().pipe(
      switchMap(api => api.getSelfSchoolId()),
      tap(schoolId => this.schoolId = schoolId)
    )
  }

  private loadStudent() {
    return this.route.parent.paramMap.pipe(
      switchMap(params =>
        this.getStudent(this.schoolId, +params.get('studentId'))
      ),
      tap<V3Student>(student => this.student = student)
    )
  }

  private getStudent(schoolId: number, studentId: number) {
    return this.provider.casa().pipe(
      switchMap(api => api.getStudent(schoolId, studentId))
    )
  }

  private loadData() {
    return this.spinnerService.trace(this.getAvailabilities('', 1)).pipe(
      map<ItemAvailability[], ItemAvailability[]>(allSetAvailabilities =>
        allSetAvailabilities.filter(it => (it.isAssigned || it.isAvailable) && this.exerciseSets.has(it.path))
      ),
      tap<ItemAvailability[]>(setAvailabilities =>
        this.setAvailabilities = setAvailabilities
      ),
      switchMap<ItemAvailability[], Observable<Observable<[V3Chapter[], ItemAvailability[], Page<V3ExerciseSession>]>>>(availabilities =>
        from(availabilities.map(availability =>
          combineLatest([
            this.loadExerciseSetStructure(availability.path),
            this.loadInnerAvailabilities(availability.path),
            this.loadSetSessionsPage(availability.path)
          ])
        ))
      ),
      mergeAll()
    )
  }

  loadInnerAvailabilities(path: string) {
    return this.getAvailabilities(path, 3).pipe(
      tap(setInnerAvailabilities => setInnerAvailabilities.forEach(av => this.innerAvailabilities.set(av.path, av)))
    );
  }

  getAvailabilities(path: string, depth: number) {
    return this.provider.cspaV3().pipe(
      switchMap(api => api.listPersonAvailabilities(this.student.person.id, path, depth))
    )
  }

  loadExerciseSetStructure(path: string): Observable<V3Chapter[]> {
    if (this.chaptersCache.has(path))
      return of(this.chaptersCache.get(path));

    return this.provider.cspaV3().pipe(
      switchMap(api => api.getChapters(`${path}_`, 3)),
      tap(setChapters => this.chaptersCache.set(path, setChapters))
    )
  }

  private loadExerciseSets(): Observable<any> {
    if (this.exerciseSets && this.exerciseSets.size > 0) return of(this.exerciseSets)
    return this.provider.cspaV3().pipe(
      switchMap(api => api.listExerciseSets()),
      map(sets =>
        sets.reduce((res, current) => res.set(current.path, current), new Map<string, V3ExerciseSet>())
      ),
      tap(setsMap => this.exerciseSets = setsMap)
    )
  }

  getSetName(availability: ItemAvailability) {
    return this.exerciseSets.get(availability.path).name;
  }

  getItemLastUpdate(availability: ItemAvailability) {
    return availability.lastUpdate;
  }

  listAvailableStages(path: string): V3Chapter[] {
    const setChapters = this.chaptersCache.get(path);
    if (!setChapters) return [];
    return setChapters.filter(
      chapter => {
        const av = this.innerAvailabilities.get(chapter.path);
        if (!av) return false;
        return av.isAvailable || av.isAssigned;
      }
    );
  }

  getItemShortName(item: StructItemBaseTyped) {
    return item.shortName;
  }

  getItemByPath(path: string) {
    return this.innerAvailabilities.get(path);
  }

  getStatusClass(path: string) {
    const availability = this.innerAvailabilities.get(path);

    if (!availability) return 'locked';
    if (!availability.isAssigned || !availability.isAvailable)
      return 'locked';
    if (availability.score === 0) return 'ready';
    else if (availability.score < 0.5) return 'bad';
    else if (availability.score < 0.95) return 'enough';
    else return 'perfect';
  }

  private loadSetSessionsPage(path: string): Observable<Page<V3ExerciseSession>> {
    return this.provider.cspaV3().pipe(
      switchMap(api => api.findSessions(this.student.person.id, `${path}_`, Pageable.of(0, 5, ["finishDate,DESC"]))),
      tap<Page<V3ExerciseSession>>(setSessions => this.setSessions.set(path, setSessions))
    );
  }

  countSetSessions(availability: ItemAvailability) {
    if (!availability) return 0;
    const setSessionsPage = this.setSessions.get(availability.path);
    if (!setSessionsPage) return 0;
    return setSessionsPage.totalElements;
  }

  getSetSessions(setAvailability: ItemAvailability): V3ExerciseSession[] {
    const setSessions = this.setSessions.get(setAvailability.path);
    if (!setSessions) return [];
    return setSessions.content;
  }

  getChapterHeader(setAvailability: ItemAvailability) {
    return this.getSetSessions(setAvailability)[0].chapterName.split(' ')[0]
  }

  getExerciseNameHeader(setAvailability: ItemAvailability) {
    return this.getSetSessions(setAvailability)[0].exerciseName.split(' ')[0]
  }

  getChapter(session: V3ExerciseSession) {
    return session.chapterName.split(' ')[1]
  }

  getSection(session: V3ExerciseSession) {
    return session.sectionName.split(',')[0]
  }

  getExerciseName(session: V3ExerciseSession) {
    return session.exerciseName.split(' ')[1]
  }

  getSessionDate(session: V3ExerciseSession) {
    return session.finishDate;
  }

  isExam(path: string) {
    return path.includes("exam")
  }
}
